import {
  useEffect,
  useState,
  Suspense,
  lazy,
  type ComponentType,
  type ReactNode,
} from 'react';
import { Switch, Route, Redirect, useLocation, Router } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, Box } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import { SideNavigation } from '@/layout/side-nav';
import {
  useNavigate,
  useAuth,
  useRedirectData,
  useSignInFromAnotherTab,
} from '@/hooks';
import { setFlash } from '@/store/flash';
import { LoginRevamp } from '@/pages/login/loginRevamp';
import { Signup } from '@/pages/signup';
import { UnSubscribe } from '@/pages/unsubscribe';
import { PublicIntroEmail } from '@/pages/intros';

import { PasswordReset } from '@/pages/password-reset';
import { AppLoading } from './AppLoading';

import { PrivateRoutes } from './PrivateRoutes';
import { useApp } from './use-app';
import dayjs from 'dayjs';
import { PublicConsent } from '@/pages/intros/PublicConsent';
import { Fallback } from './Fallback';

import { Provider } from 'react-redux';
import { ChakraProvider as ThemeProvider, extendTheme } from '@chakra-ui/react';
import { store } from '@/store';
import { UpgradeManager } from './UpgradeManager';
import { customTheme } from '../theme';

/** react-redux@7 Provider types disagree with current @types/react JSX; safe at runtime. */
const ReduxProvider = Provider as unknown as ComponentType<{
  store: typeof store;
  children?: ReactNode;
}>;

import { SetPassword } from '@/pages/onboarding/set-password';
import { useMixpanel } from '@/hooks/use-mixpanel';
import { useIsExternalUser } from '@/hooks/useIsExternalUser';
import NavigationContext from '@/layout/side-nav/context';
import { setNavLocalState, getNavLocalState } from '@/layout/side-nav/utlis';
import TopNavBarLayout from '@/layout/top-nav';
// Lazy-loaded to keep the Highcharts stack out of the entry bundle (it loaded
// on first paint for every session). All three reach ChartRenderer/highcharts
// statically: the print-only report routes via the reports subtree, and the
// AgentForge umbrella via its dashboard chart blocks. Each already renders (or
// now renders) under a <Suspense> boundary, so the highcharts chunk is deferred
// until one of these is actually used.
const GenerateReports = lazy(
  () => import('@/pages/reports/ats-reports/generate/GenerateReports')
);
const GenerateScorecard = lazy(
  () => import('@/pages/scorecards-generate/GenerateScorecard')
);
const AgentForgeV1Umbrella = lazy(() =>
  import('@/pages/agentforge/components/AgentForge').then((m) => ({
    default: m.AgentForge,
  }))
);
import { selectPermissionLoading } from '@/store/bootstrap/selectors';
import { useIsPreferencesLoading } from '@/store/ServerStorage';
import { usePosthogClient } from '@/lib/posthogClient';

// React Query - R
// READ MORE: https://tanstack.com/query/latest/docs/framework/react/overview
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import UnSubscribeRevamp from '@/pages/unsubscribe-revamp';
import { BulkOperationsProvider } from '@/components/ats/BulkOperationQueue';
import { useKFlags } from '@/hooks/use-k-flags';
import { useCallback, useMemo } from 'react';
import { MobileRouteGuard } from '@/componentsV1/MobileRouteGuard';

const SuspendedView = lazy(() =>
  import('@/components/suspended-view').then((m) => ({
    default: m.SuspendedView,
  }))
);

const PDF_GENERATION_PATHS = ['/reports/generate', '/scorecards/generate'];

const SKIP_AUTH_PATHS = [
  '/signup',
  '/password_reset',
  '/intro/addemail',
  '/intro/consent',
  '/unsubscribed',
  ...PDF_GENERATION_PATHS,
  '/unsubscribe',
];

const useShouldShowNavBars = (showNav, currUser) => {
  const currentPath = window.location.pathname;
  return useMemo(() => {
    return (
      showNav &&
      currUser &&
      !PDF_GENERATION_PATHS.includes(currentPath) &&
      !currentPath.startsWith('/rippling')
    );
  }, [showNav, currUser, currentPath]);
};

const MainApp = () => {
  const { init: initMixpanel } = useMixpanel();
  const dispatch = useDispatch();
  const location = useLocation();
  const { showNav, isLogin, isFromAuth } = useApp();
  const { goToLogin, goToHome } = useNavigate();
  const [token, setToken] = useState(null);
  const [shouldResetPassword, setResetPassword] = useState(false);
  const { ats_enabled, feAtsComponents, feAgentForgeV1 } = useKFlags();
  const {
    getAuth,
    setAuth,
    bootstrap,
    isBootstraping,
    bootstrapError,
    currUser,
    isLoggedIn,
  } = useAuth();
  const redirectData = useRedirectData();
  const [isLoading, setLoading] = useState(true);
  // Lazy initialiser: getNavLocalState() hits localStorage, and as a plain call
  // in the render body it ran on every MainApp render while only ever being
  // used to seed this state on mount.
  const [isNavCollapsed, setNavCollapsed] = useState(getNavLocalState);
  const [crachError, setError] = useState(null);
  const isExternalUser = useIsExternalUser();
  const posthog = usePosthogClient();
  const showNavBars = useShouldShowNavBars(showNav, currUser);

  /* eslint-disable react-hooks/purity -- Date.now() is intentionally impure; we need the current time to check trial expiry */
  const isTrialExpired =
    currUser?.account?.status === 'trial' &&
    dayjs(currUser?.account?.expires_at).diff(Date.now(), 'day') < 0;
  /* eslint-enable react-hooks/purity */

  const onError = (error) => {
    // If Error boundary throws ChunkLoadError, then reload the window.
    if (error.name === 'ChunkLoadError') {
      setError(error);
      return window.location.reload();
    }
    Sentry.withScope((scope) => {
      scope.setLevel('fatal');
      scope.setTag('source', 'app-error-boundary');
      Sentry.captureException(error);
    });
  };

  const onCompleteSetPassword = () => {
    setResetPassword(false);
  };

  useEffect(() => {
    if (redirectData) {
      const { data, flash, errors = [] } = redirectData;
      // Typechecking for Array here on errors object to bypass other errors related to integrations which are not related to bootstrap.
      // Those errors are handled at the integrations routes.
      // For account_integration related errors, its returning hash of hashes, so using the array check which is relevant only to user related errors.
      if (
        Array.isArray(errors) &&
        (errors?.includes('err_user_deleted') ||
          errors?.includes('err_suspended'))
      ) {
        goToLogin();
        setLoading(false);
        return;
      }

      if (flash) {
        // dispatch `setFlash` asynchronously so it runs *after* the render
        Promise.resolve().then(() => dispatch(setFlash(flash)));
      }
      // extract access token from redirect data (present in OAuth flow)
      // NOTE: in the future, route path check may be needed if other flows (besides OAuth) use redirect data
      if (data?.access_token) setAuth(data.access_token);
      if (data?.reset_password_token) {
        setToken(data?.reset_password_token);
        setResetPassword(true);
      }
    }
    if (SKIP_AUTH_PATHS.includes(location.pathname)) {
      setLoading(false);
      return;
    }
    const authToken = getAuth();
    if (!authToken) {
      goToLogin();
      setLoading(false);
      return;
    }
    bootstrap();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A tab left on the sign-in screen does not notice a sign-in that happened
  // in another one, even though they share the token.
  useSignInFromAnotherTab();

  useEffect(() => {
    if (isBootstraping !== false) return;
    if (
      bootstrapError?.includes('err_token_invalid') ||
      bootstrapError?.includes('err_user_deleted') ||
      bootstrapError?.includes('err_suspended')
    ) {
      goToLogin();
    } else if (
      isLoggedIn ||
      (isLogin && currUser) ||
      redirectData?.data?.access_token // signed in via redirect data
    ) {
      if (!isLogin) return;
      goToHome();
    }
    setLoading(false);
  }, [isBootstraping, currUser, bootstrapError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currUser) return;
    initMixpanel();

    posthog?.identify(currUser?.id, {
      email: currUser?.email,
      first_name: currUser?.first_name,
      last_name: currUser?.last_name,
      role: currUser?.role,
      is_super_admin: currUser?.is_super_admin,

      // account details (TODO: move to group once posthog moved to premium)
      account_domain: window.location?.hostname,
      account_active_users: currUser?.active_users,
      account_status: currUser?.account?.status,
      account_expires_at: currUser?.account?.expires_at,
    });
  }, [currUser, posthog]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavCollapse = useCallback((navState = false) => {
    setNavCollapsed(navState);
    setNavLocalState(navState);
  }, []);

  // NavigationContext wraps the whole authenticated shell, and its consumers
  // include the contacts and circle-contacts tables. As an inline literal the
  // value got a new identity on every MainApp render (route change, bootstrap
  // or preferences update), re-rendering all of them even though the nav had
  // not moved. Declared here rather than at the Provider because the early
  // returns below would make a hook at that point conditional.
  const navigationContextValue = useMemo(
    () => ({ isNavCollapsed, handleNavCollapse }),
    [isNavCollapsed, handleNavCollapse]
  );

  //since most pages depended on permission, show loader until permission is done
  const isPermissionLoading = useSelector(selectPermissionLoading);
  const isPreferencesLoading = useIsPreferencesLoading();

  if (isLoading || isPermissionLoading || isPreferencesLoading) {
    return <AppLoading />;
  }

  if (bootstrapError.includes('internal_server_error'))
    return <div>Something went wrong</div>;

  if (currUser?.account?.status === 'suspended' && !isFromAuth) {
    return (
      <Suspense fallback={<AppLoading />}>
        <SuspendedView />
      </Suspense>
    );
  }

  // Checking for trial users expired time and if it already expired redirecting them to suspended users
  if (isTrialExpired) {
    return (
      <Suspense fallback={<AppLoading />}>
        <SuspendedView />
      </Suspense>
    );
  }

  if (currUser && shouldResetPassword) {
    return <SetPassword token={token} onComplete={onCompleteSetPassword} />;
  }

  const defaultRoute = isExternalUser ? '/projects' : '/dashboard';

  const renderPage = () => {
    return (
      <BulkOperationsProvider>
        {/* AgentForge V1 umbrella — headless background mounts (WS subscriber,
            messages fetcher) plus the overlay surfaces that gate themselves
            on store state. Render once per authenticated session. Lazy-loaded
            (keeps Highcharts out of the entry bundle) under a null-fallback
            Suspense — it renders no first-paint UI, so deferring it a tick is
            invisible. `/login` is a Route in this same tree, so `currUser`
            keeps it from mounting — and calling AgentForge — before login. */}
        {feAgentForgeV1 && currUser ? (
          <Suspense fallback={null}>
            <AgentForgeV1Umbrella />
          </Suspense>
        ) : null}
        <Flex w="100%" h="100%" overflow="hidden">
          {showNavBars && <SideNavigation />}
          {/*
            NOTE: AgentForgeBar (product-wide bottom dock) is temporarily
            disabled. Restore the bar in `src/pages/agentforge/components/
            AgentForge.tsx` and re-enable the two `feAgentForgeV1`-gated
            blocks below (`pb` gutter on <main> and the rounded-bottom card
            wrapper on the inner <Flex>) to bring the docked layout back.
          */}
          <Box
            height="100%"
            flex="1"
            as="main"
            overflow="hidden"
            pb={
              feAgentForgeV1
                ? 'var(--agentforge-bottom-gutter, 0px)'
                : undefined
            }
          >
            <Sentry.ErrorBoundary
              fallback={<Fallback error={crachError} />}
              onError={onError}
              // This will help reset the error on path change
              // key={location.pathname} //! commenting this as this was causing some routes to be skipped
            >
              <Flex
                height="100%"
                flexDirection="column"
                // {...(feAgentForgeV1
                //   ? {
                //       borderBottomLeftRadius: '12px',
                //       borderBottomRightRadius: '12px',
                //       borderBottom: '1px solid',
                //       borderLeft: '1px solid',
                //       borderRight: '1px solid',
                //       borderColor: 'border-color',
                //       overflow: 'hidden',
                //       mx: '8px',
                //     }
                //   : {})}
              >
                <Suspense fallback={<AppLoading />}>
                  <Switch>
                    <Route exact path="/">
                      <Redirect to={defaultRoute} />
                    </Route>
                    <Route exact path="/login" component={LoginRevamp} />
                    <Route exact path="/signup" component={Signup} />
                    <Route
                      exact
                      path="/reports/generate"
                      component={GenerateReports}
                    />
                    <Route
                      exact
                      path="/scorecards/generate"
                      component={GenerateScorecard}
                    />
                    <Route
                      exact
                      path="/password_reset"
                      component={PasswordReset}
                    />
                    <Route
                      exact
                      path="/intro/addemail"
                      component={PublicIntroEmail}
                    />
                    <Route
                      exact
                      path="/intro/consent"
                      component={PublicConsent}
                    />
                    <Route exact path="/unsubscribed" component={UnSubscribe} />
                    <Route
                      exact
                      path="/unsubscribe"
                      component={UnSubscribeRevamp}
                    />
                    <PrivateRoutes />
                  </Switch>
                </Suspense>
              </Flex>
            </Sentry.ErrorBoundary>
          </Box>
        </Flex>
      </BulkOperationsProvider>
    );
  };
  return (
    <NavigationContext.Provider value={navigationContextValue}>
      {ats_enabled ? (
        <Flex
          flexDir="column"
          w="100%"
          // 100vh is the URL-bar-retracted height on mobile, so the shell
          // outgrows the screen and its last child sits behind browser chrome.
          sx={{
            height: '100vh',
            '@supports (height: 100dvh)': { height: '100dvh' },
          }}
          overflow="hidden"
          className={showNavBars && feAtsComponents ? 'scroll-hidden' : ''}
        >
          {showNavBars && <TopNavBarLayout />}
          {renderPage()}
        </Flex>
      ) : (
        renderPage()
      )}
    </NavigationContext.Provider>
  );
};

// Module scope: rebuilt in the component body, a single re-render of App would
// have thrown away the whole query cache and the router's history.
const theme = extendTheme({ ...customTheme });
const history = createBrowserHistory();

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router history={history}>
        <ThemeProvider theme={theme}>
          <ReduxProvider store={store}>
            <MobileRouteGuard>
              <MainApp />
            </MobileRouteGuard>
            {/* Outside MainApp so a stale tab is upgraded (or told) even on
                screens that never load a new chunk — the case lazyWithRetry
                cannot reach. */}
            <UpgradeManager />
          </ReduxProvider>
        </ThemeProvider>
      </Router>
    </QueryClientProvider>
  );
};
